import React, { useState } from 'react';
import { useStore } from '../store/useStore';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { TaskCard } from '../components/tasks/TaskCard';
import { TaskForm } from '../components/tasks/TaskForm';
import { ListTodo, MessageSquare, Plus, InboxIcon, SendIcon, Undo, Redo } from 'lucide-react';
import toast from 'react-hot-toast';

export const ExecutiveTasksPage: React.FC = () => {
  const { currentUser, tasks, undoTaskAction, redoTaskAction, canUndo, canRedo } = useStore();
  const [activeTab, setActiveTab] = useState<'received' | 'sent'>('received');
  const [showTaskForm, setShowTaskForm] = useState(false);

  // Get tasks assigned to the executive
  const receivedTasks = tasks.filter(task => task.assignedTo === currentUser?.id);
  
  // Get tasks assigned by the executive
  const sentTasks = tasks.filter(task => task.assignedBy === currentUser?.id);

  const handleUndo = () => {
    undoTaskAction();
    toast.success('Action undone');
  };

  const handleRedo = () => {
    redoTaskAction();
    toast.success('Action redone');
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <img
              src={currentUser?.avatar}
              alt={currentUser?.name}
              className="w-16 h-16 rounded-full ring-4 ring-emerald-500 ring-offset-4"
            />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Tasks</h1>
              <p className="text-gray-600">Manage your tasks and assignments</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={handleUndo}
              disabled={!canUndo()}
              icon={<Undo className="w-4 h-4" />}
              className="text-gray-600"
            >
              Undo
            </Button>
            <Button
              variant="ghost"
              onClick={handleRedo}
              disabled={!canRedo()}
              icon={<Redo className="w-4 h-4" />}
              className="text-gray-600"
            >
              Redo
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowTaskForm(true)}
              icon={<Plus className="w-4 h-4" />}
            >
              Create Task
            </Button>
          </div>
        </div>

        <div className="flex gap-4 mb-6">
          <Button
            variant={activeTab === 'received' ? 'primary' : 'ghost'}
            onClick={() => setActiveTab('received')}
            icon={<InboxIcon className="w-4 h-4" />}
          >
            Tasks Received ({receivedTasks.length})
          </Button>
          <Button
            variant={activeTab === 'sent' ? 'primary' : 'ghost'}
            onClick={() => setActiveTab('sent')}
            icon={<SendIcon className="w-4 h-4" />}
          >
            Tasks Sent ({sentTasks.length})
          </Button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'received' ? (
            <motion.div
              key="received"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              {receivedTasks.map(task => (
                <TaskCard key={task.id} task={task} showDepartment />
              ))}
              {receivedTasks.length === 0 && (
                <p className="text-center py-8 text-gray-500">
                  No tasks assigned to you
                </p>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="sent"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              {sentTasks.map(task => (
                <TaskCard key={task.id} task={task} showDepartment />
              ))}
              {sentTasks.length === 0 && (
                <p className="text-center py-8 text-gray-500">
                  No tasks assigned by you
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {showTaskForm && (
        <Card className="p-6">
          <TaskForm onSuccess={() => {
            setShowTaskForm(false);
            toast.success('Task created successfully');
          }} />
        </Card>
      )}
    </div>
  );
};